import { Platform } from 'react-native';
import * as WebBrowser from 'expo-web-browser';
import * as AuthSession from 'expo-auth-session';
import { supabase } from '../../shared/supabase/supabaseClient';
import { useAuthStore } from '../store/useAuthStore';
import { getPasswordResetRedirectUrl } from './authPassword';
import { logAuditEvent } from '../../shell/audit/auditLogs';
import { buildClientAuditMeta } from '../../shell/clientPresence/buildClientPresencePayload';
import { getAuthCallbackFlowTypeFromUrl, showEmailConfirmedAfterSignupToast } from './emailConfirmToast';
import { clearPasswordRecoveryFlowMarker } from './passwordRecoveryWebStorage';
import { showError } from '../../shared/ui/appToast';
import { customerPortalBaseUrl } from '../../shared/customerPortalUrl';
import { reportAuthFailure } from './authObservability';

// --- OAuth redirect URI (inlined from oauthRedirect.ts) ---

const MOBILE_FALLBACK = 'vuelto://auth/callback';

function appendNextQueryParam(uri: string, nextAfterLoginPath?: string): string {
  const path = nextAfterLoginPath?.trim();
  if (!path || !path.startsWith('/') || path.startsWith('//')) return uri;
  try {
    const u = new URL(uri);
    u.searchParams.set('next', path);
    return u.toString();
  } catch {
    return uri;
  }
}

export function getOAuthRedirectUri(nextAfterLoginPath?: string): string {
  if (Platform.OS === 'web') {
    const explicit = process.env.EXPO_PUBLIC_OAUTH_REDIRECT_URL?.trim();
    if (explicit) {
      return appendNextQueryParam(explicit.replace(/\/$/, ''), nextAfterLoginPath);
    }
    if (typeof window !== 'undefined' && window.location?.origin) {
      const origin = window.location.origin.replace(/\/$/, '');
      return appendNextQueryParam(`${origin}/auth/callback`, nextAfterLoginPath);
    }
    return AuthSession.makeRedirectUri({ path: 'auth/callback' });
  }

  const fromSession = AuthSession.makeRedirectUri({
    scheme: 'vuelto',
    path: 'auth/callback',
    preferLocalhost: false,
  });
  if (fromSession && !/localhost|127\.0\.0\.1/.test(fromSession) && fromSession.startsWith('vuelto://')) {
    return fromSession;
  }
  return MOBILE_FALLBACK;
}

WebBrowser.maybeCompleteAuthSession();

const OAUTH_RETURN_TO_STORAGE_KEY = 'vuelto_oauth_return_to';

/** Persiste `?return_to=` / `?portal_return=` en sessionStorage para no perderlo si la URL cambia antes de tocar Google/Apple. */
export function persistWebReturnToFromUrl(): void {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const rt = (params.get('return_to') ?? params.get('portal_return'))?.trim();
    if (!rt?.startsWith('/') || rt.startsWith('//')) return;

    // Links viejos del portal (?portal_return=/negocio): sesión vive en www, no en app.
    if (rt === '/negocio' || rt.startsWith('/negocio/')) {
      const portal = customerPortalBaseUrl();
      window.location.replace(`${portal}/cuenta/login?next=${encodeURIComponent(rt)}`);
      return;
    }

    sessionStorage.setItem(OAUTH_RETURN_TO_STORAGE_KEY, rt);
  } catch {
    /* ignore */
  }
}

/** Para completar el login cuando el deep link llega por navegador externo (Android). */
let pendingProvider: 'google' | 'apple' | null = null;

function isWebOAuthCallbackUrl(url: string): boolean {
  if (!url.startsWith('http://') && !url.startsWith('https://')) return false;
  try {
    const u = new URL(url);
    return u.pathname.includes('/auth/callback');
  } catch {
    return false;
  }
}

/**
 * Enlace de “olvidé mi clave” (web app o deep link). Si la tratamos como OAuth,
 * el usuario queda con sesión y pasa a POS sin tocar nunca un formulario de nueva clave.
 */
export function isPasswordRecoveryUrl(url: string): boolean {
  if (url.startsWith('vuelto://')) {
    const hash = url.includes('#') ? url.slice(url.indexOf('#') + 1) : '';
    if (hash) {
      if (hash.includes('type=recovery') || hash.includes('type%3Drecovery')) return true;
      try {
        return new URLSearchParams(hash).get('type') === 'recovery';
      } catch {
        return false;
      }
    }
    const q = url.split('?')[1]?.split('#')[0];
    if (q) {
      return new URLSearchParams(q).get('type') === 'recovery' || Boolean(new URLSearchParams(q).get('code'));
    }
    return false;
  }
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return false;
  }
  try {
    const u = new URL(url);
    if (u.pathname.includes('reset-password')) {
      if (u.searchParams.get('code') || u.searchParams.get('type') === 'recovery') return true;
    }
    if (u.searchParams.get('type') === 'recovery') {
      return true;
    }
    // No tratar `/?code=` genérico como recovery: choca con OAuth PKCE en `/` o `/auth/callback`.
    if (u.hash) {
      const h = u.hash.slice(1);
      if (h.includes('type=recovery') || h.includes('type%3Drecovery')) {
        return true;
      }
      return new URLSearchParams(h).get('type') === 'recovery';
    }
  } catch {
    return false;
  }
  return false;
}

/**
 * En nativo, el enlace de recovery debe abrirse en el navegador (PWA/HTTPS) en
 * [getPasswordResetRedirectUrl]. Convierte `vuelto://…#…` o `?code=` legacy al equivalente https.
 */
export function resolvePasswordRecoveryToPublicWebUrl(incoming: string): string {
  if (incoming.startsWith('https://') || incoming.startsWith('http://')) {
    return incoming;
  }
  const base = getPasswordResetRedirectUrl().replace(/#.*$/, '');
  if (!incoming.startsWith('vuelto://')) {
    return base;
  }
  const hashIndex = incoming.indexOf('#');
  if (hashIndex >= 0) {
    return `${base}${incoming.slice(hashIndex)}`;
  }
  const qIndex = incoming.indexOf('?');
  if (qIndex >= 0) {
    return `${base}${incoming.slice(qIndex)}`;
  }
  return base;
}

function getQueryParamFromUrl(url: string, paramName: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.searchParams.get(paramName);
  } catch {
    return null;
  }
}

/** Extrae access_token y refresh_token del hash de la URL de callback (Supabase redirige con #fragment). */
function getTokensFromRedirectUrl(url: string): { access_token: string; refresh_token: string } | null {
  const hashIndex = url.indexOf('#');
  if (hashIndex === -1) return null;
  const params = new URLSearchParams(url.slice(hashIndex + 1));
  const access_token = params.get('access_token');
  const refresh_token = params.get('refresh_token');
  if (!access_token || !refresh_token) return null;
  return { access_token, refresh_token };
}

async function completeSessionFromRedirectUrl(url: string): Promise<boolean> {
  const authStore = useAuthStore.getState();

  const tokens = getTokensFromRedirectUrl(url);
  if (tokens) {
    const { data: sessionData, error: sessionError } = await supabase.auth.setSession({
      access_token: tokens.access_token,
      refresh_token: tokens.refresh_token,
    });
    if (sessionError) throw sessionError;
    if (!sessionData.session || !sessionData.user) throw new Error('No se pudo establecer la sesión.');
    authStore.setSession(sessionData.session, sessionData.user);
    return true;
  }

  const code = getQueryParamFromUrl(url, 'code');
  if (!code) return false;

  const { data: sessionData, error: sessionError } = await supabase.auth.exchangeCodeForSession(code);
  if (sessionError) throw sessionError;
  if (!sessionData.session || !sessionData.user) throw new Error('No se pudo establecer la sesión.');
  authStore.setSession(sessionData.session, sessionData.user);
  return true;
}

/**
 * Completa el flujo OAuth cuando la app recibe el deep link.
 * También maneja errores cuando Supabase redirige a vuelto://auth/callback?error=... (Site URL sin dominio).
 * En web acepta http(s)://…/…/auth/callback (Expo Web / makeRedirectUri).
 */
export async function completeOAuthFromCallbackUrl(url: string): Promise<boolean> {
  if (!url.startsWith('vuelto://auth/callback') && !isWebOAuthCallbackUrl(url)) return false;
  const authStore = useAuthStore.getState();

  const wasEmailSignupConfirm = getAuthCallbackFlowTypeFromUrl(url) === 'signup';

  const error = getQueryParamFromUrl(url, 'error');
  if (error) {
    const errorDesc =
      getQueryParamFromUrl(url, 'error_description') ?? error ?? 'Error al iniciar sesión';
    const message = decodeURIComponent(errorDesc.replace(/\+/g, ' '));
    authStore.setError(message);
    showError('Inicio con Google u otro proveedor', message);
    reportAuthFailure('oauth_callback', new Error(`${error}: ${message}`), { title: 'Inicio con proveedor' });
    pendingProvider = null;
    return true;
  }

  const provider = pendingProvider ?? 'google';
  pendingProvider = null;
  authStore.setLoading(true);
  try {
    const completed = await completeSessionFromRedirectUrl(url);
    if (!completed) {
      if (__DEV__) console.log('[SSO] Callback sin tokens en la URL');
      return false;
    }
    if (wasEmailSignupConfirm) {
      showEmailConfirmedAfterSignupToast();
    }
    clearPasswordRecoveryFlowMarker();
    const client = await buildClientAuditMeta();
    await logAuditEvent(`login.sso.${provider}`, { client });
    if (Platform.OS === 'web' && typeof window !== 'undefined' && window.history?.replaceState) {
      // Quitar tokens del hash y dejar de mostrar /auth/callback en la barra (solo queda path limpio).
      const path = window.location.pathname;
      if (path.includes('auth/callback')) {
        window.history.replaceState({}, '', `${window.location.origin}/`);
      } else {
        const clean = `${window.location.pathname}${window.location.search}`;
        window.history.replaceState({}, '', clean);
      }
    }
    return true;
  } catch (err: any) {
    if (__DEV__) console.log('[SSO] Error en callback:', err);
    const message = err?.message ?? 'Error al completar el inicio de sesión.';
    authStore.setError(message);
    showError('Inicio con proveedor', message);
    reportAuthFailure('oauth_callback', err, { title: 'Inicio con proveedor' });
    return false;
  } finally {
    authStore.setLoading(false);
  }
}

export async function signInWithProvider(provider: 'google' | 'apple') {
  if (__DEV__) console.log('[SSO] Iniciando login con', provider);
  pendingProvider = provider;
  const authStore = useAuthStore.getState();
  authStore.setLoading(true);
  authStore.setError(undefined);

  let webOAuthFullPageRedirect = false;
  try {
    await WebBrowser.maybeCompleteAuthSession();
    if (Platform.OS === 'android') {
      await WebBrowser.warmUpAsync();
    }

    let returnToPath: string | undefined;
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const rt = (params.get('return_to') ?? params.get('portal_return'))?.trim();
      if (rt?.startsWith('/') && !rt.startsWith('//')) {
        returnToPath = rt;
        try {
          sessionStorage.removeItem(OAUTH_RETURN_TO_STORAGE_KEY);
        } catch {
          /* ignore */
        }
      } else {
        try {
          const stored = sessionStorage.getItem(OAUTH_RETURN_TO_STORAGE_KEY)?.trim();
          if (stored?.startsWith('/') && !stored.startsWith('//')) {
            returnToPath = stored;
            sessionStorage.removeItem(OAUTH_RETURN_TO_STORAGE_KEY);
          }
        } catch {
          /* ignore */
        }
      }
    }
    const redirectUri = getOAuthRedirectUri(returnToPath);
    if (__DEV__) console.log('[SSO] redirectUri:', redirectUri);
    if (__DEV__ && Platform.OS === 'web' && typeof window !== 'undefined' && redirectUri.startsWith('http')) {
      console.log(
        '[SSO] Asegurate de que esta URL esté en Supabase → Auth → Redirect URLs (y que Site URL no sea solo un scheme nativo).',
      );
    }
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider,
      options: {
        redirectTo: redirectUri,
        skipBrowserRedirect: true,
        scopes: provider === 'google' ? 'email profile openid' : undefined,
      },
    });

    if (error || !data.url) {
      if (__DEV__) console.log('[SSO] Error al obtener URL:', error?.message ?? 'sin data.url');
      throw error ?? new Error('No pudimos iniciar el flujo de SSO');
    }

    if (__DEV__) console.log('[SSO] Auth URL (copiá en el navegador de la PC para probar):', data.url);

    // En web no usamos popup: redirigimos la ventana completa al IdP
    // (popup en about:blank no funciona en todos los navegadores).
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      webOAuthFullPageRedirect = true;
      window.location.assign(data.url);
      return;
    }

    const result = await WebBrowser.openAuthSessionAsync(data.url, redirectUri, {
      preferEphemeralSession: false,
      showInRecents: true,
      ...(Platform.OS === 'android' && { createTask: true }),
    });
    const resultUrl = 'url' in result ? result.url : null;
    if (__DEV__) console.log('[SSO] openAuthSessionAsync result:', result.type, resultUrl ? '(con URL)' : '(sin URL)');

    if (result.type !== 'success' || !resultUrl) {
      throw new Error('El inicio con el proveedor fue cancelado.');
    }

    const completed = await completeSessionFromRedirectUrl(resultUrl);
    if (!completed) {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !sessionData.session) {
        throw sessionError ?? new Error('No pudimos obtener la sesión.');
      }
      authStore.setSession(sessionData.session, sessionData.session.user ?? null);
    }

    clearPasswordRecoveryFlowMarker();
    const client = await buildClientAuditMeta();
    await logAuditEvent(`login.sso.${provider}`, { client });
  } catch (err: any) {
    const message = err?.message ?? 'No pudimos completar el inicio con el proveedor.';
    authStore.setError(message);
    showError('Inicio con Google o Apple', message);
    reportAuthFailure('sso', err, { title: 'Inicio con Google o Apple' });
    if (__DEV__) console.log('[SSO] Error sign in', err);
  } finally {
    pendingProvider = null;
    if (!webOAuthFullPageRedirect) {
      authStore.setLoading(false);
    }
  }
}
